import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useResource } from "../lib/hooks";
import { useToast } from "../lib/toast";
import { VideoModelSelect } from "../components/VideoModelSelect";
import { ImageLightbox } from "../components/ImageLightbox";
import {
  ANGLE_PRESETS,
  DEFAULT_VIEW_CAMERA,
  buildNewViewPrompt,
} from "../lib/anglePrompts";
import type {
  Character,
  GenerationUsage,
  KeyframeRole,
  Project,
  ProjectDefaults,
  Scene,
  SceneKeyframe,
  SceneParameters,
  Setting,
  ComposedPrompt,
} from "@flowgen/shared";
import { DEFAULT_VIDEO_MODEL } from "@flowgen/shared";

const DEFAULT_IMAGE_MODEL = "google/gemini-2.5-flash-image";
/** Soft identity refs alongside a source keyframe — keep low so camera wins. */
const NEW_VIEW_EXTRA_REFS = 3;

const HELPER_STORYBOARD =
  "Location: xxx\nClear physical action. Camera follows the doing.";

interface SceneTemplate {
  characterIds: string[];
  settingId: string | null;
  model: string;
  storyboard: string;
  continuesFromSceneId: string | null;
  continueEnabled: boolean;
  /** After the new scene is created, patch this existing scene to continue from it. */
  linkSuccessorId?: string | null;
}

interface ArrowEdge {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function newKeyframeId(): string {
  return crypto.randomUUID().replace(/-/g, "").slice(0, 16);
}

function closeDialogState(
  setEditing: (s: Scene | null) => void,
  setCreating: (v: boolean) => void,
  setTemplate: (t: SceneTemplate | null) => void,
  setSelectedId: (id: string | null) => void
) {
  setEditing(null);
  setCreating(false);
  setTemplate(null);
  setSelectedId(null);
}

export function ScenesTab() {
  const scenes = useResource<Scene[]>(() => api.get("/scenes"), []);
  const characters = useResource<Character[]>(() => api.get("/characters"), []);
  const settings = useResource<Setting[]>(() => api.get("/settings"), []);
  const toast = useToast();
  const [editing, setEditing] = useState<Scene | null>(null);
  const [creating, setCreating] = useState(false);
  const [template, setTemplate] = useState<SceneTemplate | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [projectDefaults, setProjectDefaults] = useState<ProjectDefaults>({});
  const [edges, setEdges] = useState<ArrowEdge[]>([]);
  const graphRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());

  const busy = (scenes.data ?? []).some(
    (s) => s.status === "queued" || s.status === "generating"
  );

  const recomputeEdges = useCallback(() => {
    const graph = graphRef.current;
    const list = scenes.data;
    if (!graph || !list?.length) {
      setEdges([]);
      return;
    }
    const g = graph.getBoundingClientRect();
    const next: ArrowEdge[] = [];
    for (const s of list) {
      if (!s.continueEnabled || !s.continuesFromSceneId) continue;
      const pred = list.find((x) => x.id === s.continuesFromSceneId);
      if (!pred) continue;
      const fromEl = cardRefs.current.get(pred.id);
      const toEl = cardRefs.current.get(s.id);
      if (!fromEl || !toEl) continue;
      const a = fromEl.getBoundingClientRect();
      const b = toEl.getBoundingClientRect();
      next.push({
        key: `${pred.id}->${s.id}`,
        x1: a.left + a.width / 2 - g.left,
        y1: a.bottom - g.top,
        x2: b.left + b.width / 2 - g.left,
        y2: b.top - g.top,
      });
    }
    setEdges(next);
  }, [scenes.data]);

  useLayoutEffect(() => {
    recomputeEdges();
  }, [recomputeEdges, selectedId]);

  useEffect(() => {
    const onResize = () => recomputeEdges();
    window.addEventListener("resize", onResize);
    const graph = graphRef.current;
    const ro = graph ? new ResizeObserver(() => recomputeEdges()) : null;
    if (graph && ro) ro.observe(graph);
    return () => {
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [recomputeEdges]);

  useEffect(() => {
    if (!busy) return;
    const id = window.setInterval(() => {
      scenes.reload({ silent: true });
    }, 1000);
    return () => window.clearInterval(id);
  }, [busy, scenes.reload]);

  useEffect(() => {
    const id = api.getActiveProjectId();
    if (!id) return;
    api.get<Project>(`/projects/${id}`)
      .then((p) => setProjectDefaults(p.defaults ?? {}))
      .catch(() => setProjectDefaults({}));
  }, [creating, editing]);

  function startDependent(base: Scene, direction: "forward" | "backward") {
    const shared = {
      characterIds: [...(base.characterIds ?? [])],
      settingId: base.settingId ?? null,
      model: (base.model ?? "").trim(),
      storyboard: HELPER_STORYBOARD,
      continueEnabled: true as const,
    };
    if (direction === "forward") {
      setTemplate({
        ...shared,
        continuesFromSceneId: base.id,
      });
    } else {
      setTemplate({
        ...shared,
        continuesFromSceneId: null,
        linkSuccessorId: base.id,
      });
    }
    setEditing(null);
    setCreating(true);
  }

  function clearDialog() {
    closeDialogState(setEditing, setCreating, setTemplate, setSelectedId);
  }

  return (
    <div>
      <div className="workspace-header">
        <div>
          <h2>Scenes</h2>
          <div className="sub">Compose storyboards that pull in your characters and places.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button
            className="primary"
            onClick={() => {
              setTemplate(null);
              setCreating(true);
            }}
          >
            New scene
          </button>
        </div>
      </div>

      {scenes.loading && !scenes.data && <div className="muted">Loading...</div>}
      {scenes.error && <div className="empty">{scenes.error}</div>}
      {!scenes.loading && !scenes.error && (scenes.data?.length ?? 0) === 0 && (
        <div className="empty">No scenes yet. Start by writing a storyboard.</div>
      )}

      {scenes.data && scenes.data.length > 0 && (
        <div className="scene-graph" ref={graphRef}>
          <svg className="scene-arrows" aria-hidden>
            <defs>
              <marker
                id="scene-arrowhead"
                markerWidth="8"
                markerHeight="8"
                refX="6"
                refY="4"
                orient="auto"
              >
                <path d="M0,0 L8,4 L0,8 Z" fill="var(--accent)" opacity="0.7" />
              </marker>
            </defs>
            {edges.map((e) => {
              const dy = Math.max(40, Math.abs(e.y2 - e.y1) * 0.45);
              const d = `M ${e.x1} ${e.y1} C ${e.x1} ${e.y1 + dy}, ${e.x2} ${e.y2 - dy}, ${e.x2} ${e.y2}`;
              return (
                <path
                  key={e.key}
                  d={d}
                  fill="none"
                  stroke="var(--accent)"
                  strokeOpacity="0.55"
                  strokeWidth="1.5"
                  markerEnd="url(#scene-arrowhead)"
                />
              );
            })}
          </svg>
          <div className="grid cards">
            {scenes.data.map((s) => {
              const linkedChars = (s.characterIds ?? [])
                .map((id) => characters.data?.find((c) => c.id === id))
                .filter(Boolean) as Character[];
              const linkedSetting = settings.data?.find((x) => x.id === s.settingId) ?? null;
              const localSrc = s.videoUrl ? api.mediaUrl(s.videoUrl) : "";
              const pred = s.continuesFromSceneId
                ? scenes.data?.find((x) => x.id === s.continuesFromSceneId)
                : null;
              const kfCount = s.keyframes?.length ?? 0;
              const selected = selectedId === s.id;
              return (
                <div
                  key={s.id}
                  className={`card scene-card${selected ? " selected" : ""}`}
                  ref={(el) => {
                    if (el) cardRefs.current.set(s.id, el);
                    else cardRefs.current.delete(s.id);
                  }}
                  onClick={(e) => {
                    const t = e.target as HTMLElement;
                    if (t.closest("button, a, video, input, select, textarea")) return;
                    setSelectedId((cur) => (cur === s.id ? null : s.id));
                  }}
                >
                  {selected && (
                    <>
                      <button
                        type="button"
                        className="scene-link-btn scene-link-btn-left"
                        onClick={(e) => {
                          e.stopPropagation();
                          startDependent(s, "backward");
                        }}
                      >
                        ← New scene before
                      </button>
                      <button
                        type="button"
                        className="scene-link-btn scene-link-btn-right"
                        onClick={(e) => {
                          e.stopPropagation();
                          startDependent(s, "forward");
                        }}
                      >
                        New scene after →
                      </button>
                    </>
                  )}
                  {s.status === "ready" && localSrc && (
                    <video
                      className="scene-card-video"
                      src={localSrc}
                      controls
                      playsInline
                      preload="metadata"
                    />
                  )}
                  {(s.status === "queued" || s.status === "generating") && (
                    <SceneRunStatus scene={s} />
                  )}
                  <div className="row between">
                    <h3>{s.title}</h3>
                    <span className={`status-pill ${s.status}`}>{s.status}</span>
                  </div>
                  <div className="meta">{s.storyboard.slice(0, 140)}{s.storyboard.length > 140 ? "..." : ""}</div>
                  <div className="tiny muted">
                    {s.durationSeconds}s · {linkedSetting ? linkedSetting.name : "No setting"}
                    {pred && s.continueEnabled ? ` · continues from ${pred.title}` : ""}
                    {kfCount ? ` · ${kfCount} keyframe${kfCount === 1 ? "" : "s"}` : ""}
                    {s.originalVideoUrl
                      ? ` · cut ${((s.trimStartSeconds ?? 0) + (s.trimEndSeconds ?? 0)).toFixed(1)}s`
                      : ""}
                  </div>
                  {s.status === "ready" && s.usage && (
                    <div className="tiny muted">{formatUsageLine(s.usage)}</div>
                  )}
                  <div>
                    {linkedChars.map((c) => <span key={c.id} className="tag">{c.name}</span>)}
                    {!linkedChars.length && <span className="muted tiny">No characters</span>}
                  </div>
                  {s.status === "failed" && s.externalVideoUrl && (
                    <div>
                      <video
                        className="scene-card-video"
                        src={s.externalVideoUrl}
                        controls
                        playsInline
                        crossOrigin="anonymous"
                      />
                      <div className="tiny muted" style={{ marginTop: 4 }}>
                        Playback is served by OpenRouter directly.{" "}
                        <a href={s.externalVideoUrl} target="_blank" rel="noreferrer" style={{ color: "var(--accent)" }}>
                          Open in new tab ↗
                        </a>
                        .
                      </div>
                    </div>
                  )}
                  {s.status === "failed" && (
                    <div className="tiny" style={{ color: "var(--danger)" }}>
                      {s.errorMessage ?? "Generation failed"}
                    </div>
                  )}
                  <div className="row" style={{ gap: 4 }}>
                    <button onClick={() => setEditing(s)}>Edit</button>
                    <button className="danger" onClick={async () => {
                      if (!confirm("Delete scene?")) return;
                      await api.delete(`/scenes/${s.id}`);
                      scenes.reload({ silent: true });
                    }}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(creating || editing) && characters.data && settings.data && scenes.data && (
        <SceneDialog
          initial={editing}
          template={creating ? template : null}
          characters={characters.data}
          settings={settings.data}
          scenes={scenes.data}
          projectDefaults={projectDefaults}
          onClose={clearDialog}
          onSaved={() => {
            const wasEdit = !!editing;
            clearDialog();
            scenes.reload();
            toast.show(wasEdit ? "Scene updated" : "Scene created", "success");
          }}
          onSavedAndQueued={() => {
            clearDialog();
            scenes.reload({ silent: true });
            toast.show("Saved and queued generation", "success");
          }}
        />
      )}
    </div>
  );
}

function SceneRunStatus({ scene }: { scene: Scene }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const startedMs = scene.generationStartedAt
    ? Date.parse(scene.generationStartedAt)
    : null;
  const elapsed =
    startedMs && Number.isFinite(startedMs) ? formatElapsed(now - startedMs) : null;

  const nextCheckAt = scene.progress?.nextCheckAt ?? null;
  const countdownSec =
    typeof nextCheckAt === "number" && nextCheckAt > now
      ? Math.max(0, Math.ceil((nextCheckAt - now) / 1000))
      : null;

  let message =
    scene.progress?.message ??
    (scene.status === "queued" ? "Queued…" : "Generating…");

  if (countdownSec != null && scene.progress?.stage === "checking_download") {
    message = `Checking download in ${countdownSec} second${countdownSec === 1 ? "" : "s"}…`;
  }

  if (scene.status === "queued") {
    return (
      <div className="scene-card-video-placeholder scene-run-status">
        <div className="scene-run-message">Queued…</div>
      </div>
    );
  }

  const usage = scene.progress?.usage ?? scene.usage;

  return (
    <div className="scene-card-video-placeholder scene-run-status">
      <div className="scene-run-timer">{elapsed ?? "0:00"}</div>
      <div className="scene-run-message">{message}</div>
      {usage && (usage.tokensIn != null || usage.tokensOut != null || usage.costUsd != null) && (
        <div className="scene-run-usage">{formatUsageLine(usage)}</div>
      )}
    </div>
  );
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatUsageLine(u: GenerationUsage): string {
  const bits: string[] = [];
  if (u.tokensIn != null) bits.push(`in ${formatTokens(u.tokensIn)}`);
  if (u.tokensOut != null) bits.push(`out ${formatTokens(u.tokensOut)}`);
  if (u.costUsd != null) bits.push(formatUsd(u.costUsd));
  return bits.join(" · ");
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(Math.round(n));
}

function formatUsd(n: number): string {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(3)}`;
}

interface SceneDialogProps {
  initial: Scene | null;
  template?: SceneTemplate | null;
  characters: Character[];
  settings: Setting[];
  scenes: Scene[];
  projectDefaults: ProjectDefaults;
  onClose: () => void;
  onSaved: () => void;
  onSavedAndQueued: () => void;
}

function SceneDialog({
  initial,
  template = null,
  characters,
  settings,
  scenes,
  projectDefaults,
  onClose,
  onSaved,
  onSavedAndQueued,
}: SceneDialogProps) {
  const toast = useToast();
  const [tab, setTab] = useState<"options" | "keyframes">("options");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [storyboard, setStoryboard] = useState(
    initial?.storyboard ?? template?.storyboard ?? ""
  );
  const [duration, setDuration] = useState(
    initial?.durationSeconds === 4 || initial?.durationSeconds === 8
      ? initial.durationSeconds
      : 6
  );
  const [model, setModel] = useState(() => {
    const m = (initial?.model ?? template?.model ?? "").trim();
    if (!m || m === "openrouter/video-gen-default" || m === "default") return "";
    return m;
  });
  const [settingId, setSettingId] = useState<string | null>(
    initial?.settingId ?? template?.settingId ?? null
  );
  const [characterIds, setCharacterIds] = useState<string[]>(
    initial?.characterIds ?? template?.characterIds ?? []
  );
  const [params, setParams] = useState<SceneParameters>(initial?.parameters ?? {});
  const [continuesFromSceneId, setContinuesFromSceneId] = useState<string | null>(
    initial?.continuesFromSceneId ?? template?.continuesFromSceneId ?? null
  );
  const [continueEnabled, setContinueEnabled] = useState(
    initial?.continueEnabled ?? template?.continueEnabled ?? true
  );
  const [keyframes, setKeyframes] = useState<SceneKeyframe[]>(initial?.keyframes ?? []);
  const [preview, setPreview] = useState<ComposedPrompt | null>(null);
  const [saving, setSaving] = useState(false);
  const [kfPrompt, setKfPrompt] = useState("");
  const [kfModel, setKfModel] = useState(DEFAULT_IMAGE_MODEL);
  const [kfBusy, setKfBusy] = useState(false);
  const [viewSourceId, setViewSourceId] = useState<string>("");
  const [viewPrompt, setViewPrompt] = useState(DEFAULT_VIEW_CAMERA);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const predecessorOptions = scenes.filter((s) => s.id !== initial?.id);
  const predecessor = continuesFromSceneId
    ? scenes.find((s) => s.id === continuesFromSceneId)
    : null;
  const hasFirstKeyframe = keyframes.some((k) => k.role === "first");

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (model) return;
    let alive = true;
    api.get<{ defaultModel: string }>("/config/openrouter").then((s) => {
      if (!alive) return;
      setModel(s.defaultModel?.trim() || DEFAULT_VIDEO_MODEL);
    });
    return () => { alive = false; };
  }, [model]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!storyboard.trim()) {
        setPreview(null);
        return;
      }
      try {
        const res = await api.post<ComposedPrompt>(
          "/scenes/preview",
          sceneBody()
        );
        setPreview(res);
      } catch {
        /* ignore */
      }
    }, 400);
    return () => clearTimeout(t);
  }, [title, storyboard, duration, model, settingId, characterIds, params, continuesFromSceneId, continueEnabled, keyframes]);

  function sceneBody() {
    return {
      title: title.trim() || "Untitled",
      storyboard,
      durationSeconds: duration,
      model,
      settingId,
      characterIds,
      parameters: {
        aspectRatio: params.aspectRatio,
        motionIntensity: params.motionIntensity,
        negativePrompt: params.negativePrompt,
        generateAudio: params.generateAudio,
        fps: params.fps,
        seed: params.seed,
      },
      continuesFromSceneId,
      continueEnabled,
      keyframes,
    };
  }

  function toggleCharacter(id: string) {
    setCharacterIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  }

  function setKeyframeRole(id: string, role: KeyframeRole) {
    setKeyframes((cur) =>
      cur.map((k) => {
        if (k.id === id) return { ...k, role };
        if ((role === "first" || role === "last") && k.role === role) {
          return { ...k, role: "none" };
        }
        return k;
      })
    );
  }

  function toggleKeyframeRef(id: string) {
    setKeyframes((cur) =>
      cur.map((k) => (k.id === id ? { ...k, useAsReference: !k.useAsReference } : k))
    );
  }

  function removeKeyframe(id: string) {
    setKeyframes((cur) => cur.filter((k) => k.id !== id));
  }

  async function persist(andGenerate: boolean) {
    if (!title.trim() || !storyboard.trim()) {
      toast.show("Title and storyboard are required", "error");
      return;
    }
    setSaving(true);
    try {
      const body = sceneBody();
      let scene: Scene;
      if (initial) {
        scene = await api.patch<Scene>(`/scenes/${initial.id}`, body);
      } else {
        scene = await api.post<Scene>("/scenes", body);
        if (template?.linkSuccessorId) {
          await api.patch(`/scenes/${template.linkSuccessorId}`, {
            continuesFromSceneId: scene.id,
            continueEnabled: true,
          });
        }
      }
      if (andGenerate) {
        await api.post(`/scenes/${scene.id}/generate`);
        onSavedAndQueued();
      } else {
        onSaved();
      }
    } catch (err: any) {
      toast.show(err?.message ?? "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  function identityRefUrls(extra: string[] = [], maxTotal = 6): string[] {
    const urls: string[] = [...extra];
    for (const id of characterIds) {
      const c = characters.find((x) => x.id === id);
      if (!c) continue;
      for (const u of c.referenceImageUrls) {
        if (!urls.includes(u)) urls.push(u);
      }
    }
    if (settingId) {
      const s = settings.find((x) => x.id === settingId);
      if (s) {
        for (const u of s.referenceImageUrls) {
          if (!urls.includes(u)) urls.push(u);
        }
      }
    }
    return urls.slice(0, maxTotal);
  }

  async function generateKeyframe() {
    if (!kfPrompt.trim()) {
      toast.show("Enter a keyframe prompt", "error");
      return;
    }
    setKfBusy(true);
    try {
      const refs = identityRefUrls();
      const res = await api.post<{ url: string }>("/media/generate-image", {
        prompt: kfPrompt.trim(),
        model: kfModel.trim() || DEFAULT_IMAGE_MODEL,
        referenceImageUrls: refs.length ? refs : undefined,
      });
      setKeyframes((cur) => [
        ...cur,
        {
          id: newKeyframeId(),
          url: res.url,
          useAsReference: true,
          role: "none",
        },
      ]);
      toast.show("Keyframe added", "success");
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Generate failed", "error");
    } finally {
      setKfBusy(false);
    }
  }

  async function generateNewView() {
    const src = keyframes.find((k) => k.id === viewSourceId);
    if (!src) {
      toast.show("Pick a source keyframe", "error");
      return;
    }
    if (!viewPrompt.trim()) {
      toast.show("Describe the new camera view", "error");
      return;
    }
    setKfBusy(true);
    try {
      // Source keyframe first; few identity extras so CAMERA instruction wins.
      const refs = identityRefUrls([src.url], 1 + NEW_VIEW_EXTRA_REFS);
      const res = await api.post<{ url: string }>("/media/generate-image", {
        prompt: buildNewViewPrompt(viewPrompt),
        model: kfModel.trim() || DEFAULT_IMAGE_MODEL,
        referenceImageUrls: refs,
      });
      setKeyframes((cur) => [
        ...cur,
        {
          id: newKeyframeId(),
          url: res.url,
          useAsReference: true,
          role: "none",
        },
      ]);
      toast.show("New view keyframe added", "success");
    } catch (err: any) {
      toast.show(err?.details?.message ?? err?.message ?? "Generate failed", "error");
    } finally {
      setKfBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={{ width: 920, maxHeight: "92vh" }} onClick={(e) => e.stopPropagation()}>
        <h3>{initial ? "Edit scene" : "New scene"}</h3>

        <div className="field">
          <label>Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field-row">
          <div>
            <label>Duration (s)</label>
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              <option value={4}>4</option>
              <option value={6}>6</option>
              <option value={8}>8</option>
            </select>
          </div>
          <div>
            <label>Model</label>
            <VideoModelSelect value={model} onChange={setModel} />
          </div>
        </div>
        <div className="field">
          <label>Storyboard</label>
          <textarea
            value={storyboard}
            onChange={(e) => setStoryboard(e.target.value)}
            rows={6}
            placeholder="Describe the scene action, camera angles, pacing, and any dialogue or beats."
          />
        </div>

        <div className="ref-tabs" aria-label="Scene editor sections">
          <button
            type="button"
            className={`ref-tab ${tab === "options" ? "active" : ""}`}
            onClick={() => setTab("options")}
          >
            Scene Options
          </button>
          <button
            type="button"
            className={`ref-tab ${tab === "keyframes" ? "active" : ""}`}
            onClick={() => setTab("keyframes")}
          >
            Keyframe Generation
          </button>
        </div>

        {tab === "options" && (
          <div className="scene-composer">
            <div>
              <div className="field">
                <label>Continue from previous scene</label>
                <select
                  value={continuesFromSceneId ?? ""}
                  onChange={(e) => {
                    const id = e.target.value || null;
                    setContinuesFromSceneId(id);
                    if (id) setContinueEnabled(true);
                  }}
                >
                  <option value="">None</option>
                  {predecessorOptions.map((s) => (
                    <option key={s.id} value={s.id} disabled={s.status !== "ready" && !s.videoUrl && !s.lastFrameUrl}>
                      {s.title}
                      {s.status === "ready" ? "" : ` (${s.status})`}
                      {s.lastFrameUrl ? " · has last frame" : s.videoUrl ? " · has video" : ""}
                    </option>
                  ))}
                </select>
                {continuesFromSceneId && (
                  <label className="row" style={{ gap: 8, marginTop: 8, alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={continueEnabled}
                      onChange={(e) => setContinueEnabled(e.target.checked)}
                      disabled={hasFirstKeyframe}
                    />
                    <span className="tiny">
                      Use last frame as first frame
                      {hasFirstKeyframe ? " (disabled — a keyframe is set as first)" : ""}
                    </span>
                  </label>
                )}
                {continueEnabled && !hasFirstKeyframe && predecessor?.lastFrameUrl && (
                  <div className="continuity-frame" style={{ marginTop: 8 }}>
                    <div className="tiny muted" style={{ marginBottom: 4 }}>
                      Locked first frame from “{predecessor.title}”
                    </div>
                    <img
                      src={api.mediaUrl(predecessor.lastFrameUrl)}
                      alt="Previous scene last frame"
                      style={{
                        width: "100%",
                        maxHeight: 120,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid var(--border)",
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="field-row">
                <div>
                  <label>Aspect ratio</label>
                  <select
                    value={params.aspectRatio ?? ""}
                    onChange={(e) => setParams((p) => ({ ...p, aspectRatio: (e.target.value || undefined) as SceneParameters["aspectRatio"] }))}
                  >
                    <option value="">
                      {projectDefaults.aspectRatio
                        ? `Project (${projectDefaults.aspectRatio})`
                        : "Default"}
                    </option>
                    <option value="16:9">16:9</option>
                    <option value="9:16">9:16</option>
                    <option value="1:1">1:1</option>
                    <option value="4:3">4:3</option>
                  </select>
                </div>
                <div>
                  <label>Motion</label>
                  <select
                    value={params.motionIntensity ?? ""}
                    onChange={(e) => setParams((p) => ({ ...p, motionIntensity: (e.target.value || undefined) as SceneParameters["motionIntensity"] }))}
                  >
                    <option value="">
                      {projectDefaults.motionIntensity
                        ? `Project (${projectDefaults.motionIntensity})`
                        : "Default"}
                    </option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={!!params.generateAudio}
                  onChange={(e) =>
                    setParams((p) => ({ ...p, generateAudio: e.target.checked || undefined }))
                  }
                />
                Generate audio with video
              </label>
            </div>

            <div className="refs">
              <div className="field">
                <label>Setting</label>
                <select value={settingId ?? ""} onChange={(e) => setSettingId(e.target.value || null)}>
                  <option value="">No setting</option>
                  {settings.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label>Characters</label>
                <div className="picker-list">
                  {characters.length === 0 && <div className="muted tiny">No characters yet</div>}
                  {characters.map((c) => {
                    const checked = characterIds.includes(c.id);
                    return (
                      <label key={c.id} className={`picker-item ${checked ? "checked" : ""}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCharacter(c.id)}
                        />
                        <span style={{ flex: 1 }}>{c.name}</span>
                        <span className="tiny muted">{c.referenceImageUrls.length} ref</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="field">
                <label>Generated prompt</label>
                <div className="preview">
                  {preview ? preview.text : "(Storyboard is empty)"}
                </div>
                {preview && (
                  <div className="tiny muted" style={{ marginTop: 6 }}>
                    Refs: {preview.referenceImageUrls.length}
                    {(preview.frameImages?.length ?? 0) > 0
                      ? ` · frames: ${preview.frameImages!.map((f) => f.frameType).join(", ")}`
                      : ""}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "keyframes" && (
          <div>
            <div className="tiny muted" style={{ marginBottom: 8 }}>
              Generate stills, mark first / middle / last, and check soft refs. Tip: make two complementary angles → set first + last → Generate video (orbit/push between them). Prefer modest moves (+30°) before a full reverse.
            </div>

            <div className="field">
              <label>Keyframe prompt</label>
              <textarea
                value={kfPrompt}
                onChange={(e) => setKfPrompt(e.target.value)}
                rows={3}
                placeholder="Describe a keyframe still (composition, pose, lens)…"
                disabled={kfBusy}
              />
            </div>
            <div className="field-row">
              <div>
                <label>Image model</label>
                <input
                  value={kfModel}
                  onChange={(e) => setKfModel(e.target.value)}
                  disabled={kfBusy}
                />
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button className="primary" disabled={kfBusy || !kfPrompt.trim()} onClick={generateKeyframe}>
                  {kfBusy ? "Working…" : "Generate keyframe"}
                </button>
              </div>
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <label>Generate new view from keyframe</label>
              <select
                value={viewSourceId}
                onChange={(e) => setViewSourceId(e.target.value)}
                disabled={kfBusy || keyframes.length === 0}
              >
                <option value="">Pick source keyframe…</option>
                {keyframes.map((k, i) => (
                  <option key={k.id} value={k.id}>
                    #{i + 1}{k.role !== "none" ? ` (${k.role})` : ""}
                  </option>
                ))}
              </select>
              <div className="tiny muted" style={{ marginTop: 8, marginBottom: 4 }}>
                Angle presets (edit the camera line after picking)
              </div>
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                {ANGLE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={kfBusy}
                    onClick={() => setViewPrompt(p.camera)}
                    style={{ fontSize: 11, padding: "3px 8px" }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <textarea
                style={{ marginTop: 8 }}
                value={viewPrompt}
                onChange={(e) => setViewPrompt(e.target.value)}
                rows={3}
                placeholder="CAMERA instruction: shot size, angle, height…"
                disabled={kfBusy}
              />
              <button
                style={{ marginTop: 8 }}
                disabled={kfBusy || !viewSourceId || !viewPrompt.trim()}
                onClick={generateNewView}
              >
                Generate new view
              </button>
            </div>

            {keyframes.length === 0 ? (
              <div className="empty" style={{ minHeight: 80, marginTop: 12 }}>No keyframes yet.</div>
            ) : (
              <div className="image-grid" style={{ marginTop: 12, gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
                {keyframes.map((k, idx) => (
                  <div key={k.id} className="keyframe-card">
                    <img
                      src={api.mediaUrl(k.url)}
                      alt=""
                      onClick={() => setLightboxIndex(idx)}
                      style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 6, cursor: "zoom-in" }}
                    />
                    <label className="row" style={{ gap: 6, marginTop: 6, fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={k.useAsReference}
                        onChange={() => toggleKeyframeRef(k.id)}
                      />
                      Use as ref
                    </label>
                    <select
                      value={k.role}
                      onChange={(e) => setKeyframeRole(k.id, e.target.value as KeyframeRole)}
                      style={{ marginTop: 4, width: "100%", fontSize: 12 }}
                    >
                      <option value="none">Role: none</option>
                      <option value="first">First frame</option>
                      <option value="middle">Middle</option>
                      <option value="last">Last frame</option>
                    </select>
                    <button
                      className="danger"
                      style={{ marginTop: 4, width: "100%", fontSize: 11, padding: "2px 6px" }}
                      onClick={() => removeKeyframe(k.id)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {lightboxIndex != null && (
              <ImageLightbox
                urls={keyframes.map((k) => k.url)}
                index={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                onIndexChange={setLightboxIndex}
              />
            )}
          </div>
        )}

        <div className="row between" style={{ marginTop: 16, gap: 8, flexWrap: "wrap" }}>
          <button onClick={onClose} disabled={saving || kfBusy}>Cancel</button>
          <div className="row" style={{ gap: 8 }}>
            <button disabled={saving || kfBusy} onClick={() => persist(false)}>
              {saving ? "Saving…" : initial ? "Save" : "Create"}
            </button>
            <button
              className="primary"
              disabled={saving || kfBusy}
              onClick={() => persist(true)}
            >
              {saving ? "Working…" : "Generate video"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
